import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import DetailedStatsCards from "../../analytics/components/DetailedStatsCards";
import EarningsCard from "../../analytics/components/EarningsCard";
import GeneralStatsCard from "../../analytics/components/GeneralStatsCard";
import ImpressionRevenueOverTime from "../../analytics/components/ImpressionRevenueOverTime";
import ImpressionsOverTime from "../../analytics/components/ImpressionsOverTime";
import RentalsOverTime from "../../analytics/components/RentalsOverTime";
import ROIOverTime from "../../analytics/components/ROIOverTime";
import SalesOverTime from "../../analytics/components/SalesOverTime";
import SalesStatsCard from "../../analytics/components/SalesStatsCard";
import TotalEarningsOverTime from "../../analytics/components/TotalEarningsOverTime";
import DateRangeSelector from "../../common/components/DateRangeSelector";
import SortableCardWrapper from "../../common/components/SortableCardWrapper";
import { toast } from "../../common/components/Toast";
import { ASSET_ANALYTICS_CARDS } from "../../common/constants/cardRegistry";
import { usePreviewMode } from "../../common/contexts/PreviewModeContext";
import { useSettings } from "../../common/contexts/SettingsContext";
import { saveMyPreferences } from "../../settings/api/displayTemplates";
import { getAssetAnalytics } from "../api/analytics";
import SalesDetails from "./SalesDetails";

// Build registry lookup
const REGISTRY_MAP = Object.fromEntries(
  ASSET_ANALYTICS_CARDS.map((c) => [c.key, c]),
);

// Cards that render inside the over-time charts Grid
const CHART_GRID_KEYS = new Set([
  "sales_over_time",
  "rentals_over_time",
  "impressions_over_time",
  "impression_revenue_over_time",
  "total_earnings_over_time",
  "roi_over_time",
]);

// Cards that render inside the stats Grid
const STATS_GRID_KEYS = new Set(["sales_stats_card", "general_stats_card"]);

const AssetAnalytics = ({ assetId }) => {
  const { assetAnalyticsCardConfig, updateCardChartType } = useSettings();
  const { isPreviewMode, updateCardConfig } = usePreviewMode();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const periodStart = params.get("period_start");
    const periodEnd = params.get("period_end");

    const period_range = {
      period_start: periodStart,
      period_end: periodEnd,
    };

    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const fetchedAnalytics = await getAssetAnalytics(assetId, period_range);
        setAnalytics(fetchedAnalytics);
      } catch (error) {
        toast.error(error.message || "Failed to fetch analytics");
      } finally {
        setLoading(false);
      }
    };

    if (assetId) {
      fetchAnalytics();
    }
  }, [assetId, location.search]);

  const handleChartTypeChange = useCallback(
    async (cardKey, newType) => {
      if (isPreviewMode) {
        updateCardConfig("asset_analytics_config", cardKey, {
          chart_type: newType,
        });
        return;
      }

      updateCardChartType("asset_analytics_config", cardKey, newType);
      const updated = assetAnalyticsCardConfig.map((c) =>
        c.key === cardKey ? { ...c, chart_type: newType } : c,
      );
      try {
        await saveMyPreferences({ asset_analytics_config: updated });
      } catch {
        // Silently fail
      }
    },
    [
      assetAnalyticsCardConfig,
      updateCardChartType,
      isPreviewMode,
      updateCardConfig,
    ],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !isPreviewMode) return;

      const oldIndex = assetAnalyticsCardConfig.findIndex(
        (c) => c.key === active.id,
      );
      const newIndex = assetAnalyticsCardConfig.findIndex(
        (c) => c.key === over.id,
      );
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = [...assetAnalyticsCardConfig];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);
      reordered.forEach((card, i) => {
        updateCardConfig("asset_analytics_config", card.key, { order: i + 1 });
      });
    },
    [assetAnalyticsCardConfig, isPreviewMode, updateCardConfig],
  );

  // Map card keys to render functions
  const ASSET_CARD_COMPONENTS = {
    sales_over_time: ({ analytics: a, cardConfig }) => (
      <SalesOverTime
        analytics={a}
        chartType={cardConfig.chart_type}
        allowedChartTypes={REGISTRY_MAP[cardConfig.key]?.allowedChartTypes}
        onChartTypeChange={(type) =>
          handleChartTypeChange(cardConfig.key, type)
        }
        cardKey={cardConfig.key}
        configKey="asset_analytics_config"
      />
    ),
    rentals_over_time: ({ analytics: a, cardConfig }) => (
      <RentalsOverTime
        analytics={a}
        chartType={cardConfig.chart_type}
        allowedChartTypes={REGISTRY_MAP[cardConfig.key]?.allowedChartTypes}
        onChartTypeChange={(type) =>
          handleChartTypeChange(cardConfig.key, type)
        }
        cardKey={cardConfig.key}
        configKey="asset_analytics_config"
      />
    ),
    impressions_over_time: ({ analytics: a, cardConfig }) => (
      <ImpressionsOverTime
        analytics={a}
        chartType={cardConfig.chart_type}
        allowedChartTypes={REGISTRY_MAP[cardConfig.key]?.allowedChartTypes}
        onChartTypeChange={(type) =>
          handleChartTypeChange(cardConfig.key, type)
        }
        cardKey={cardConfig.key}
        configKey="asset_analytics_config"
      />
    ),
    impression_revenue_over_time: ({ analytics: a, cardConfig }) => (
      <ImpressionRevenueOverTime
        analytics={a}
        chartType={cardConfig.chart_type}
        allowedChartTypes={REGISTRY_MAP[cardConfig.key]?.allowedChartTypes}
        onChartTypeChange={(type) =>
          handleChartTypeChange(cardConfig.key, type)
        }
        cardKey={cardConfig.key}
        configKey="asset_analytics_config"
      />
    ),
    total_earnings_over_time: ({ analytics: a, cardConfig }) => (
      <TotalEarningsOverTime
        analytics={a}
        chartType={cardConfig.chart_type}
        allowedChartTypes={REGISTRY_MAP[cardConfig.key]?.allowedChartTypes}
        onChartTypeChange={(type) =>
          handleChartTypeChange(cardConfig.key, type)
        }
        cardKey={cardConfig.key}
        configKey="asset_analytics_config"
      />
    ),
    roi_over_time: ({ analytics: a, cardConfig }) => (
      <ROIOverTime
        analytics={a}
        chartType={cardConfig.chart_type}
        allowedChartTypes={REGISTRY_MAP[cardConfig.key]?.allowedChartTypes}
        onChartTypeChange={(type) =>
          handleChartTypeChange(cardConfig.key, type)
        }
        cardKey={cardConfig.key}
        configKey="asset_analytics_config"
      />
    ),
    sales_stats_card: ({ analytics: a }) => <SalesStatsCard analytics={a} />,
    general_stats_card: ({ analytics: a }) => (
      <GeneralStatsCard analytics={a} showAssetCount={false} />
    ),
    earnings_card: ({ analytics: a }) => <EarningsCard analytics={a} />,
    detailed_stats_cards: ({ analytics: a }) => (
      <DetailedStatsCards analytics={a} />
    ),
    sales_details: ({ analytics: a }) => <SalesDetails sales={a?.sales} />,
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!analytics) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography color="text.secondary">
          No analytics data available for this asset.
        </Typography>
      </Box>
    );
  }

  const visibleCards = assetAnalyticsCardConfig.filter((c) => c.visible);
  const chartCards = visibleCards.filter((c) => CHART_GRID_KEYS.has(c.key));
  const statsCards = visibleCards.filter((c) => STATS_GRID_KEYS.has(c.key));
  const otherCards = visibleCards.filter(
    (c) => !CHART_GRID_KEYS.has(c.key) && !STATS_GRID_KEYS.has(c.key),
  );

  const renderCard = (cardConfig) => {
    const Component = ASSET_CARD_COMPONENTS[cardConfig.key];
    if (!Component) return null;
    const rendered = (
      <Component
        key={cardConfig.key}
        analytics={analytics}
        cardConfig={cardConfig}
      />
    );
    if (isPreviewMode) {
      return (
        <SortableCardWrapper key={cardConfig.key} id={cardConfig.key}>
          {rendered}
        </SortableCardWrapper>
      );
    }
    return rendered;
  };

  const cardContent = (
    <>
      {chartCards.length > 0 && (
        <Grid container columnSpacing={3} sx={{ mb: 3 }}>
          {chartCards.map((cardConfig) => renderCard(cardConfig))}
        </Grid>
      )}

      {statsCards.length > 0 && (
        <Grid container spacing={3}>
          {statsCards.map((cardConfig) => renderCard(cardConfig))}
        </Grid>
      )}

      {otherCards.map((cardConfig) => renderCard(cardConfig))}
    </>
  );

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
        <DateRangeSelector />
      </Box>

      {isPreviewMode ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={visibleCards.map((c) => c.key)}
            strategy={rectSortingStrategy}
          >
            {cardContent}
          </SortableContext>
        </DndContext>
      ) : (
        cardContent
      )}
    </Box>
  );
};

export default AssetAnalytics;
